// Merged libms StatusBadge (7-tone structure + dot) + exams styling discipline, rebuilt on daisyUI 5 badge-soft; the libms STATUS_TONE domain map + statusTone() were deliberately dropped (status→tone mapping belongs to each module), purple tone renamed → secondary, dot is opt-in and drawn with bg-current.
//
// FILLED, NEVER OUTLINED (foundations spec §12): a status surface always carries
// a fill of its own semantic colour. `badge-soft` is the default — daisyUI mixes
// the tone into base-100 at 8% and paints the text in the tone, so the chip is
// tinted, not a bare coloured border. `solid` escalates to the fully saturated
// `badge-<tone>` + its *-content colour for the loudest states (a blocked card,
// a failed settlement). `badge-outline` is never used here.
import { Show, type Component } from 'solid-js';
import { Dynamic } from 'solid-js/web';

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary' | 'secondary';

/* Complete static class strings per tone — never assembled from fragments. */
const TONE_SOFT: Record<Tone, string> = {
  success: 'badge-soft badge-success',
  warning: 'badge-soft badge-warning',
  error: 'badge-soft badge-error',
  info: 'badge-soft badge-info',
  neutral: 'badge-soft badge-neutral',
  primary: 'badge-soft badge-primary',
  secondary: 'badge-soft badge-secondary',
};

const TONE_SOLID: Record<Tone, string> = {
  success: 'badge-success',
  warning: 'badge-warning',
  error: 'badge-error',
  info: 'badge-info',
  neutral: 'badge-neutral',
  primary: 'badge-primary',
  secondary: 'badge-secondary',
};

/**
 * A read-only chip for one record's STATE. It capitalises, so a raw enum
 * value can go straight in; a category (a code, a locale, a subject) is a
 * `Tag` from @erp/ui/tag instead — status and category are two vocabularies
 * (DEV-GUIDE-002 §12.4 / §12.5).
 */
export function StatusBadge(props: {
  /** The state, often the wire value — underscores become spaces and CSS capitalises each word. */
  label: string;
  /** The semantic role from the module's own status→tone map. Default neutral. */
  tone?: Tone;
  /** A leading status dot in the tone's ink. */
  dot?: boolean;
  /** A lucide-solid icon COMPONENT before the label, 12px, decorative — for the one state a glyph says faster than a word ("verified", "locked"). */
  icon?: Component<{ size?: number; class?: string }>;
  /** Saturated fill + *-content text, for the states that must interrupt. */
  solid?: boolean;
  /** Automation id on the chip. */
  id?: string;
  class?: string;
}) {
  const toneClass = () =>
    props.solid ? TONE_SOLID[props.tone ?? 'neutral'] : TONE_SOFT[props.tone ?? 'neutral'];
  return (
    <span
      id={props.id}
      class={`badge badge-sm gap-1.5 whitespace-nowrap font-medium capitalize ${toneClass()} ${
        props.class ?? ''
      }`}
    >
      <Show when={props.dot}>
        <span class="size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
      </Show>
      <Show when={props.icon}>
        {(icon) => (
          <span class="flex shrink-0">
            <Dynamic component={icon()} size={12} aria-hidden="true" />
          </span>
        )}
      </Show>
      {props.label.replace(/_/g, ' ')}
    </span>
  );
}
